import { useCallback, useEffect, useRef, useState } from 'react';

// How long the copy affordance holds its success signal before reverting.
export const COPIED_FEEDBACK_MS = 1500;

export interface UseCopiedFlag {
  copied: boolean;
  copy: (text: string) => Promise<void>;
}

// Success-gated copy feedback: the flag rises only once the clipboard write RESOLVES, so a
// refused or unavailable clipboard shows nothing rather than a check that lied.
export function useCopiedFlag(): UseCopiedFlag {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // Clear a pending revert on unmount so it never fires on a gone component.
  useEffect(
    () => () => {
      clearTimeout(timer.current);
    },
    [],
  );

  const copy = useCallback(async (text: string): Promise<void> => {
    try {
      // Inside the try so an absent clipboard (an insecure context throws on the property access)
      // degrades the same way a refused write does.
      await navigator.clipboard.writeText(text);
    } catch {
      // The absent flag is this affordance's only failure signal. Never rejects: call sites fire
      // copy() from an onClick with `void`, where a rejection would go unhandled.
      return;
    }
    setCopied(true);
    // Re-arm from scratch: a re-copy that inherited the previous revert would blink the signal
    // off mid-window.
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      setCopied(false);
    }, COPIED_FEEDBACK_MS);
  }, []);

  return { copied, copy };
}
